// src/components/viewer/ReadOnlyShareView.tsx
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ReadOnlyHeader } from './ReadOnlyHeader';
import { TravelMap } from '../map/TravelMap';
import { CategoryPills } from '../map/CategoryPills';
import { TimelineSlider } from '../map/TimelineSlider';
import { LightboxViewer } from './LightboxViewer';
import { HydratedTripData } from '../../services/remoteHydration';
import {
  photosToGeoJson,
  splitRouteAtAntiMeridian,
  computeCategoryCounts,
  computeTripTimeBounds,
} from '../../services/mapGeoJson';
import { Compass, AlertTriangle, RefreshCw, Sparkles } from 'lucide-react';
import { generateSampleTrip } from '../../services/sampleTrip';
import { sortPhotosChronologically } from '../../services/storage';

export interface ReadOnlyShareViewProps {
  hydratedData: HydratedTripData | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onExitReadOnly: () => void;
  manifestUrl?: string | null;
}

export const ReadOnlyShareView: React.FC<ReadOnlyShareViewProps> = ({
  hydratedData,
  isLoading,
  error,
  onRetry,
  onExitReadOnly,
  manifestUrl = '',
}) => {
  // Local fallback demo mode if requested
  const [demoActive, setDemoActive] = useState<boolean>(false);
  const demoData = useMemo(() => {
    if (!demoActive) return null;
    const demo = generateSampleTrip();
    return {
      tripName: demo.trip.name || 'Madeira Explorer Demo',
      timezoneId: demo.trip.timezone_id || 'Atlantic/Madeira',
      startDate: demo.trip.start_date,
      endDate: demo.trip.end_date,
      photos: demo.photos,
      locations: demo.locations,
      steps: demo.steps,
      manifestUrl: 'demo://sampleTrip',
    };
  }, [demoActive]);

  const activeTrip = demoData || hydratedData;

  const [selectedCategory, setSelectedCategory] = useState<string>('Tutte');
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [lightboxPhotoId, setLightboxPhotoId] = useState<string | null>(null);

  const photos = useMemo(() => {
    return sortPhotosChronologically(activeTrip?.photos || []);
  }, [activeTrip?.photos]);
  const locations = activeTrip?.locations || [];
  const steps = activeTrip?.steps || [];

  const [minTripTime, maxTripTime] = useMemo(() => {
    return computeTripTimeBounds(locations, photos);
  }, [locations, photos]);

  const [timeRange, setTimeRange] = useState<[number, number]>([minTripTime, maxTripTime]);

  useEffect(() => {
    setTimeRange([minTripTime, maxTripTime]);
  }, [minTripTime, maxTripTime]);

  const categoryStats = useMemo(() => {
    return computeCategoryCounts(photos);
  }, [photos]);

  const photosGeoJson = useMemo(() => {
    return photosToGeoJson(photos, {
      filter: { category: selectedCategory, timeRange },
    });
  }, [photos, selectedCategory, timeRange]);

  const routeGeoJson = useMemo(() => {
    const feature = splitRouteAtAntiMeridian(locations, { timeRange });
    return {
      type: 'FeatureCollection' as const,
      features: [feature],
    };
  }, [locations, timeRange]);

  const filteredPhotos = useMemo(() => {
    const list = photos.filter((p) => {
      if (selectedCategory !== 'Tutte' && p.category !== selectedCategory) return false;
      if (p.timestampUtc < timeRange[0] || p.timestampUtc > timeRange[1]) return false;
      return true;
    });
    return sortPhotosChronologically(list);
  }, [photos, selectedCategory, timeRange]);

  const handlePhotoSelect = useCallback((id: string) => {
    setSelectedPhotoId(id);
  }, []);

  const handlePhotoExpand = useCallback((id: string) => {
    setLightboxPhotoId(id);
    setLightboxOpen(true);
  }, []);

  // 1. Loading State
  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-slate-900 text-slate-100 font-sans items-center justify-center p-6 select-none" data-testid="remote-loading-spinner">
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400 shadow-xl shadow-sky-500/10">
            <Compass className="w-8 h-8 animate-spin" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-bold text-white">Caricamento del viaggio...</h2>
            <p className="text-xs text-slate-400 font-mono break-all">{manifestUrl || 'Download manifest in corso'}</p>
          </div>
        </div>
      </div>
    );
  }

  // 2. Error Fallback State
  if (error && !activeTrip) {
    return (
      <div className="flex flex-col h-full bg-slate-900 text-slate-100 font-sans items-center justify-center p-6 select-none" data-testid="remote-error-card">
        <div className="w-full max-w-md bg-slate-950/80 border border-red-900/60 rounded-2xl p-6 shadow-2xl backdrop-blur-md text-center space-y-4">
          <div className="w-12 h-12 rounded-xl bg-red-500/15 border border-red-500/30 text-red-400 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Impossibile caricare il viaggio</h3>
            <p className="text-xs text-red-300/90 mt-1 leading-relaxed">{error}</p>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <button
              type="button"
              onClick={onRetry}
              className="w-full py-2.5 px-4 bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold rounded-xl flex items-center justify-center gap-2 transition shadow-md cursor-pointer"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Riprova</span>
            </button>

            <button
              type="button"
              onClick={() => setDemoActive(true)}
              className="w-full py-2.5 px-4 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl flex items-center justify-center gap-2 border border-slate-700 transition cursor-pointer"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>Esplora Viaggio Demo (Madeira)</span>
            </button>

            <button
              type="button"
              onClick={onExitReadOnly}
              className="w-full py-2 px-4 text-slate-400 hover:text-white text-xs transition cursor-pointer mt-1"
            >
              Torna alla Modalità Personale
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!activeTrip) {
    return null;
  }

  // 3. Normal Read-Only Presentation Mode
  return (
    <div className="flex flex-col h-full bg-slate-900 text-slate-100 overflow-hidden font-sans select-none" data-testid="read-only-share-view">
      <ReadOnlyHeader
        tripName={activeTrip.tripName}
        startDate={activeTrip.startDate}
        endDate={activeTrip.endDate}
        photoCount={photos.length}
        onExitReadOnly={onExitReadOnly}
        manifestUrl={manifestUrl || activeTrip.manifestUrl}
      />

      <main className="flex-1 relative overflow-hidden">
        {/* Vector Map */}
        <TravelMap
          photosGeoJson={photosGeoJson}
          routeGeoJson={routeGeoJson}
          selectedPhotoId={selectedPhotoId}
          onPhotoSelect={handlePhotoSelect}
          onPhotoExpand={handlePhotoExpand}
          className="w-full h-full"
        />

        {/* Top Category Filter Bar */}
        <div className="absolute top-3 left-3 right-3 z-30 flex flex-col gap-2 pointer-events-none">
          {categoryStats.categories.length > 0 && (
            <CategoryPills
              categories={categoryStats.categories}
              counts={categoryStats.counts}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />
          )}
        </div>

        {/* Bottom Timeline Range Slider */}
        <div className="absolute bottom-4 left-3 right-3 z-30 pointer-events-none max-w-2xl mx-auto">
          <TimelineSlider
            minTime={minTripTime}
            maxTime={maxTripTime}
            currentTimeRange={timeRange}
            onChange={setTimeRange}
            steps={steps}
            timezoneId={activeTrip.timezoneId}
          />
        </div>

        {/* Fullscreen PhotoSwipe v5 Lightbox */}
        {lightboxOpen && (
          <LightboxViewer
            photos={filteredPhotos.length > 0 ? filteredPhotos : photos}
            initialPhotoId={lightboxPhotoId ?? undefined}
            isOpen={lightboxOpen}
            onClose={() => {
              setLightboxOpen(false);
              setLightboxPhotoId(null);
            }}
            onPhotoChange={(photo) => {
              setSelectedPhotoId(photo.id);
            }}
            timezoneId={activeTrip.timezoneId}
            onLocateOnMap={(photo) => {
              setSelectedPhotoId(photo.id);
            }}
          />
        )}
      </main>
    </div>
  );
};

export default ReadOnlyShareView;
